import React from "react";
import { OppgaveType } from "../enums/oppgaveType";
import { OppgaveStatus } from "../enums/oppgaveStatus";
import type { HNOppgaveFrontend } from "../types/Resources";
import type { Oppgave } from "HNEntities/oppgave";

import Button from "@helsenorge/designsystem-react/components/Button";
import Icon from "@helsenorge/designsystem-react/components/Icon";
import PdfFile from "@helsenorge/designsystem-react/components/Icons/PdfFile";
import HTMLFile from "@helsenorge/designsystem-react/components/Icons/HTMLFile";
import { createDateString } from "./date-utils";
import { SkjulSamtaleStatus } from "enums/skjulSamtaleStatus";
import { leggTilEnDag } from "./leggTilEnDag";
import { shortDateNumbers } from "@helsenorge/core-utils/date-fns-utils";
import { erInformasjonOppgave } from "./oppgaveTypeUtils";
import { erVerktoyOppgaveSelector } from "store/selectors/selectors-oppgaveDetaljer";

export const getStatusText = (
  oppgaveStatus: OppgaveStatus,
  resources: HNOppgaveFrontend | undefined,
  statusReason?: string,
  fullfortDato?: Date,
): string => {
  let stringToReturn = "";
  switch (oppgaveStatus) {
    case OppgaveStatus.Ready:
      stringToReturn = resources?.oppgavestatus_ready_ny || "";
      break;
    case OppgaveStatus.InProgress:
      stringToReturn = resources?.oppgavestatus_inProgress || "";
      break;
    case OppgaveStatus.Completed:
      if (fullfortDato) {
        const fullfortDatoString = createDateString(fullfortDato, "dd.MM.yyyy");
        stringToReturn = `${resources?.oppgavestatus_completed || ""}, ${fullfortDatoString}`;
      } else {
        stringToReturn = resources?.oppgavestatus_completed || "";
      }
      break;
    case OppgaveStatus.Cancelled:
    case OppgaveStatus.EnteredInError:
      stringToReturn = resources?.oppgavestatus_cancelled || "";
      break;
    case OppgaveStatus.Expired:
      stringToReturn = "Fristen er passert";
      break;
  }
  if (statusReason) {
    stringToReturn += ` - ${statusReason}`;
  }
  return stringToReturn;
};

export const getStatusDotVariant = (
  oppgaveStatus: OppgaveStatus,
): "transparent" | "inprocess" | "success" | "cancelled" | "noaccess" => {
  switch (oppgaveStatus) {
    case OppgaveStatus.Ready:
      return "transparent";
    case OppgaveStatus.InProgress:
      return "inprocess";
    case OppgaveStatus.Completed:
      return "success";
    case OppgaveStatus.Cancelled:
    case OppgaveStatus.EnteredInError:
      return "cancelled";
    case OppgaveStatus.Expired:
      return "noaccess";
    default:
      return "transparent";
  }
};

export const getOppgaveInstruksjon = (
  oppgaveType: OppgaveType,
  resources: HNOppgaveFrontend | undefined,
) => {
  switch (oppgaveType) {
    case OppgaveType.Skjema:
      return resources?.instruksjon_skjemaoppgave || "";
    case OppgaveType.Verktoy:
      return resources?.instruksjon_verktoystartoppgave || "";
    case OppgaveType.Betaling:
      return resources?.instruksjon_betalingsoppgave || "";
    case OppgaveType.Samtykke:
      return (
        resources?.instruksjon_samtykkeforesporsel ||
        "Svare på samtykkeforespørsel"
      );
    case OppgaveType.Ekstern:
      return resources?.instruksjon_ekstern || "";
    default:
      return resources?.instruksjon_informasjonsoppgave || "";
  }
};

export const getSkjemaSvarContent = (
  oppgave: Oppgave,
  onClick: () => void,
): React.ReactElement | string => {
  if (
    oppgave.OppgaveSvar &&
    oppgave.OppgaveSvar.MimeType === "application/pdf"
  ) {
    return (
      <Button variant="borderless" onClick={onClick}>
        <Icon svgIcon={PdfFile} />
        <div>{oppgave.OppgaveSvar.Navn}</div>
      </Button>
    );
  } else if (
    oppgave.OppgaveSvar &&
    oppgave.OppgaveSvar.MimeType === "text/html"
  ) {
    return (
      <Button variant="borderless" onClick={onClick}>
        <Icon svgIcon={HTMLFile} />
        <div>{oppgave.OppgaveSvar.Navn}</div>
      </Button>
    );
  }
  return "";
};

export const getShowClientErrorMessage = (
  hasLoadError: boolean,
  loadErrorStatusCode: number | undefined,
) => {
  return (
    hasLoadError &&
    loadErrorStatusCode !== undefined &&
    loadErrorStatusCode >= 400 &&
    loadErrorStatusCode < 500
  );
};

export const getModalDescription = (
  skjulSamtaleStatus: SkjulSamtaleStatus,
  oppgave: Oppgave | null | undefined,
  resources: HNOppgaveFrontend | undefined,
) => {
  if (skjulSamtaleStatus === SkjulSamtaleStatus.Success) {
    if (
      oppgave?.Type === OppgaveType.Skjema &&
      oppgave.Status === OppgaveStatus.Completed
    ) {
      const textArray = [
        resources?.modal_fullmakt_skjemaoppgave_success || "",
        resources?.modal_fullmakt_skjemaoppgave_success_2 || "",
      ].join("\n\n");
      return textArray;
    }
    return resources?.modal_fullmakt_description_success || "";
  } else if (skjulSamtaleStatus === SkjulSamtaleStatus.Failed) {
    if (
      oppgave?.Type === OppgaveType.Skjema &&
      oppgave.Status === OppgaveStatus.Completed
    ) {
      return resources?.modal_fullmakt_skjemaoppgave_failed || "";
    }
    return resources?.modal_fullmakt_description_failed || "";
  }
  return "";
};

export const getSlettModalDescription = (
  oppgave: Oppgave | null | undefined,
  resources: HNOppgaveFrontend | undefined,
): string => {
  if (
    oppgave?.Type === OppgaveType.Skjema &&
    oppgave.Status === OppgaveStatus.Completed
  ) {
    const textArray = [
      resources?.modal_slett_skjemaoppgave || "",
      resources?.modal_slett_description || "",
    ].join("\n\n");
    return textArray;
  }
  return resources?.modal_slett_description || "";
};

export const getOppgaveTypeTekst = (
  oppgave: Oppgave,
  resources: HNOppgaveFrontend | undefined,
) => {
  if (!resources) return "";
  switch (oppgave?.Type) {
    case OppgaveType.Skjema:
      return oppgave.Status === OppgaveStatus.Ready
        ? resources.buttontext_ready_skjema
        : resources.buttontext_inprogress_skjema;
    case OppgaveType.Samtykke:
      return resources.buttontext_ready_samtykke;
    case OppgaveType.Verktoy:
      return resources.buttontext_ready_verktoy;
    case OppgaveType.Informasjon:
      return resources.buttontext_ready_informasjon;
    case OppgaveType.Betaling:
      return resources.buttontext_ready_betaling;
    default:
      return oppgave?.Status === OppgaveStatus.Ready
        ? resources.buttontext_ready_generisk
        : resources.buttontext_inprogress_generisk;
  }
};

export const getOppgaveStatusTekst = (
  oppgave: Oppgave,
  resources: HNOppgaveFrontend | undefined,
) => {
  if (!resources) return "";
  let statusTekst = resources.oppgavestatus_ready;
  if (!oppgave) return statusTekst;
  const isExpired: boolean = leggTilEnDag(oppgave?.Frist) < new Date();

  switch (oppgave?.Status) {
    case OppgaveStatus.InProgress:
      statusTekst = resources.oppgavestatus_inProgress;
      break;
    case OppgaveStatus.Completed:
      statusTekst = oppgave.FullfortDato
        ? `${resources.oppgavestatus_completed}, ${shortDateNumbers(new Date(oppgave.FullfortDato))}`
        : `${resources.oppgavestatus_completed}`;
      break;
    case OppgaveStatus.Cancelled:
    case OppgaveStatus.EnteredInError:
      statusTekst = resources.oppgavestatus_cancelled;
      break;
    default:
      statusTekst = erInformasjonOppgave(oppgave)
        ? resources.oppgavestatus_ready_informasjon
        : resources.oppgavestatus_ready;
      break;
  }

  if (
    isExpired &&
    (oppgave?.Status === OppgaveStatus.Ready ||
      oppgave?.Status === OppgaveStatus.InProgress)
  ) {
    statusTekst = resources.label_statusexpired;
  }

  return statusTekst;
};

export const getOppgaveFristText = (
  oppgave: Oppgave,
  resources: HNOppgaveFrontend | undefined,
) => {
  if (!resources) return "";
  return erVerktoyOppgaveSelector(oppgave)
    ? resources.label_tilgjengeligTil
    : resources.label_frist;
};
